import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { StatusPill } from "@/components/StatusPill";
import { useI18n } from "@/i18n/I18nContext";
import { Wrench, Server, Boxes, ShieldCheck, Wallet, Activity, Play } from "lucide-react";

const TABS = [
  { key: "registry", label: "pt.tabRegistry", icon: Wrench },
  { key: "providers", label: "pt.tabProviders", icon: Server },
  { key: "capabilities", label: "pt.tabCapabilities", icon: Boxes },
  { key: "permissions", label: "pt.tabPermissions", icon: ShieldCheck },
  { key: "budgets", label: "pt.tabBudgets", icon: Wallet },
  { key: "health", label: "pt.tabHealth", icon: Activity },
  { key: "runs", label: "pt.tabRuns", icon: Play },
];

export default function ProductionTools() {
  const [tab, setTab] = useState("registry");
  const [data, setData] = useState(null);
  const { t } = useI18n();

  useEffect(() => {
    setData(null);
    const map = {
      registry: api.ptRegistry, providers: api.ptProviders, capabilities: api.ptCapabilities,
      permissions: api.ptPermissions, budgets: api.ptBudgets, health: api.ptHealth, runs: api.ptRuns,
    };
    map[tab]().then(setData).catch(() => setData([]));
  }, [tab]);

  return (
    <div className="space-y-6">
      <div>
        <p className="mono-label">{t("pt.label")}</p>
        <h1 className="font-display text-3xl font-extrabold text-white mt-1 flex items-center gap-2"><Wrench className="text-[#2563EB]" /> {t("pt.title")}</h1>
        <p className="text-sm text-[#94A3B8] mt-1">{t("pt.subtitle")}</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {TABS.map((tb) => (
          <button key={tb.key} onClick={() => setTab(tb.key)} data-testid={`pt-tab-${tb.key}`}
            className={`h-9 px-4 rounded-full text-xs font-medium flex items-center gap-1.5 ${tab === tb.key ? "bg-[#2563EB] text-white" : "bg-[#0B0F19] text-[#94A3B8] border border-[#1E293B]"}`}>
            <tb.icon size={13} /> {t(tb.label)}
          </button>
        ))}
      </div>

      {!data ? <p className="text-[#64748B] text-sm">{t("common.loading")}</p> : (
        <div className="m-card p-5" data-testid={`pt-panel-${tab}`}>
          {data.length === 0 && <p className="text-sm text-[#64748B]">{t("pt.noRecords")}</p>}

          {tab === "registry" && data.map((tl) => (
            <div key={tl.id} className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-3 mb-2">
              <div className="flex items-center justify-between"><span className="text-white text-sm">{tl.name} <span className="text-[#64748B] font-mono text-[10px]">· {tl.tool_code}</span></span><div className="flex gap-1.5"><StatusPill status={tl.risk_class} /><StatusPill status={tl.health_status} /></div></div>
              <p className="text-[11px] text-[#64748B] mt-1">{tl.category} · {tl.description}</p>
              <p className="text-[10px] text-[#475569] mt-1">{t("pt.capabilities", { v: (tl.capabilities || []).join(", ") })}</p>
            </div>
          ))}

          {tab === "providers" && data.map((p) => (
            <div key={p.id} className="flex items-center justify-between bg-[#0B0F19] border border-[#1E293B] rounded-xl p-3 mb-2 text-sm">
              <span className="text-white">{p.provider_name} <span className="text-[10px] text-[#64748B] font-mono">· {p.api_type}</span></span><StatusPill status={p.health_status} />
            </div>
          ))}

          {tab === "capabilities" && (
            <div className="flex flex-wrap gap-2">{data.map((c) => <span key={c.id} className="m-pill" style={{ color: "#67E8F9", borderColor: "#0891B2", background: "#083344" }}>{c.code}</span>)}</div>
          )}

          {tab === "permissions" && data.map((p) => (
            <div key={p.id} className="flex items-center justify-between bg-[#0B0F19] border border-[#1E293B] rounded-xl p-2.5 mb-1.5 text-xs">
              <span className="text-white">{p.agent_code} · {p.capability}</span>
              <span className="text-[#94A3B8]">{p.permission} · scope {(p.project_scope || []).join(",")} · risk {p.risk_limit}{p.requires_approval ? " · approval" : ""} · {p.enabled ? "ON" : "OFF"}</span>
            </div>
          ))}

          {tab === "budgets" && data.map((b) => (
            <div key={b.id} className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-3 mb-2 text-xs text-[#CBD5E1]">
              {t("pt.budgetLine", { code: b.project_code, a: b.per_tool_run_limit, b: b.per_task_limit, c: b.per_mission_limit, d: b.per_project_daily_limit, e: b.per_project_monthly_limit })}
            </div>
          ))}

          {tab === "health" && data.map((h) => (
            <div key={h.id} className="flex items-center justify-between bg-[#0B0F19] border border-[#1E293B] rounded-xl p-2.5 mb-1.5 text-xs"><span className="text-[#94A3B8] font-mono">{h.tool_id?.slice(0, 8)}</span><StatusPill status={h.status} /></div>
          ))}

          {tab === "runs" && data.map((r) => (
            <div key={r.id} className="flex items-center justify-between bg-[#0B0F19] border border-[#1E293B] rounded-xl p-2.5 mb-1.5 text-xs">
              <span className="text-[#CBD5E1]">{r.agent_code} · {(r.requested_capabilities || []).join(",")} · {r.model || "—"} · ${r.cost || 0}</span><StatusPill status={r.status} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
